import {
  getConferenceDateLabels,
  getConferenceEventBranding,
} from '../services/conferenceSettingsService';
import { resolveInvitationAssetUrl } from './invitationLetterUtils';

export const CERTIFICATE_TYPES = ['participant', 'presenter', 'exhibitor'];

export const CERTIFICATE_TYPE_LABELS = {
  participant: 'Participant',
  presenter: 'Presenter',
  exhibitor: 'Exhibitor',
};

export const CERTIFICATE_PLACEHOLDERS = [
  { key: 'recipientName', label: 'Recipient full name' },
  { key: 'conferenceTitle', label: 'Conference title' },
  { key: 'conferenceDates', label: 'Conference dates' },
  { key: 'conferenceLocation', label: 'Conference location' },
  { key: 'certificateNumber', label: 'Certificate number' },
  { key: 'issueDate', label: 'Issue date' },
  { key: 'roleLine', label: 'Role line (e.g. As a Delegate)' },
  { key: 'presentationTitle', label: 'Presentation title (presenter)' },
  { key: 'presentationType', label: 'Presentation type (oral/poster)' },
];

const buildDefaultType = ({ title, roleLine, bodyHtml }) => ({
  title,
  subtitle: 'This is to certify that',
  bodyHtml,
  roleLine,
  logoUrl: '/images/tda-logo.png',
  signatureImage: '/images/tda-president-signature.png',
  stampImage: '/images/tda-official-stamp.png',
  signatoryName: 'Dr. Gemma Z. Berege',
  signatoryTitle: 'President – Tanzania Dental Association',
});

export const DEFAULT_CERTIFICATE_SETTINGS = {
  available_after_conference_end: true,
  require_check_in: false,
  enabled_types: {
    participant: true,
    presenter: true,
    exhibitor: true,
  },
  types: {
    participant: buildDefaultType({
      title: 'CERTIFICATE OF PARTICIPATION',
      roleLine: 'As a Delegate',
      bodyHtml:
        '<p>has participated in the <strong>{{conferenceTitle}}</strong> held on {{conferenceDates}} at {{conferenceLocation}}.</p>',
    }),
    presenter: buildDefaultType({
      title: 'CERTIFICATE OF PRESENTATION',
      roleLine: 'As a Presenter',
      bodyHtml:
        '<p>has presented <strong>{{presentationTitle}}</strong> ({{presentationType}}) at the <strong>{{conferenceTitle}}</strong> held on {{conferenceDates}} at {{conferenceLocation}}.</p>',
    }),
    exhibitor: buildDefaultType({
      title: 'CERTIFICATE OF EXHIBITION',
      roleLine: 'As an Exhibitor',
      bodyHtml:
        '<p>has exhibited at the <strong>{{conferenceTitle}}</strong> held on {{conferenceDates}} at {{conferenceLocation}}.</p>',
    }),
  },
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const mergeCertificateSettings = (stored = {}) => {
  const merged = {
    ...DEFAULT_CERTIFICATE_SETTINGS,
    ...stored,
    enabled_types: {
      ...DEFAULT_CERTIFICATE_SETTINGS.enabled_types,
      ...(stored.enabled_types || {}),
    },
    types: {},
  };

  CERTIFICATE_TYPES.forEach((type) => {
    merged.types[type] = {
      ...DEFAULT_CERTIFICATE_SETTINGS.types[type],
      ...(stored.types?.[type] || {}),
    };
  });

  return merged;
};

export const generateCertificateNumber = (type = 'participant') => {
  const prefix = String(type).substring(0, 3).toUpperCase();
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `TDA-${prefix}-${timestamp}-${random}`;
};

export const formatIssueDate = (issueDate) => {
  const date = issueDate ? new Date(issueDate) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export const isConferenceEnded = (conferenceEnd) => {
  if (!conferenceEnd) return false;
  const end = new Date(conferenceEnd);
  if (Number.isNaN(end.getTime())) return false;
  end.setHours(23, 59, 59, 999);
  return new Date() > end;
};

export const getRegistrationType = (registration = {}) =>
  registration.registration_type || 'participant';

export const buildCertificatePlaceholderMap = (data = {}) => ({
  recipientName: data.recipientName || '',
  conferenceTitle: data.conferenceTitle || '',
  conferenceDates: data.conferenceDates || '',
  conferenceLocation: data.conferenceLocation || '',
  certificateNumber: data.certificateNumber || '',
  issueDate: data.issueDateFormatted || data.issueDate || '',
  roleLine: data.roleLine || '',
  presentationTitle: data.presentationTitle || '',
  presentationType: data.presentationType || '',
});

export const interpolateCertificateHtml = (html, data = {}) => {
  const map = buildCertificatePlaceholderMap(data);
  return String(html || '').replace(/\{\{(\w+)\}\}/g, (match, key) => {
    if (!(key in map)) return match;
    return escapeHtml(map[key]);
  });
};

export const buildRecipientName = (registration = {}) =>
  [registration.first_name, registration.middle_name, registration.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

export const getAcceptedAbstract = (abstracts = []) =>
  abstracts.find((abstract) => abstract.status === 'accepted') || null;

export const buildCertificateData = ({
  registration = {},
  certificateType = 'participant',
  conferenceSettings = {},
  certificateSettings = {},
  abstracts = [],
  certificateNumber,
}) => {
  const mergedSettings = mergeCertificateSettings(certificateSettings);
  const typeConfig = mergedSettings.types[certificateType] || mergedSettings.types.participant;
  const dateLabels = getConferenceDateLabels(conferenceSettings);
  const branding = getConferenceEventBranding(conferenceSettings);
  const acceptedAbstract = getAcceptedAbstract(abstracts);
  const issueDate = new Date().toISOString().split('T')[0];
  const certNumber = certificateNumber || generateCertificateNumber(certificateType);

  const base = {
    certificateType,
    typeConfig,
    recipientName: buildRecipientName(registration),
    conferenceTitle: `${branding.heroTitle} ${branding.heroSubtitle}`.trim(),
    conferenceDates: dateLabels.receipt || dateLabels.long,
    conferenceLocation: dateLabels.location,
    certificateNumber: certNumber,
    issueDate,
    issueDateFormatted: formatIssueDate(issueDate),
    roleLine: typeConfig.roleLine || '',
    presentationTitle: acceptedAbstract?.title || '',
    presentationType: acceptedAbstract?.presentation_type || 'oral',
    logoUrl: resolveInvitationAssetUrl(typeConfig.logoUrl),
    signatureImage: resolveInvitationAssetUrl(typeConfig.signatureImage),
    stampImage: resolveInvitationAssetUrl(typeConfig.stampImage),
    signatoryName: typeConfig.signatoryName || '',
    signatoryTitle: typeConfig.signatoryTitle || '',
    title: typeConfig.title || '',
    subtitle: typeConfig.subtitle || '',
    bodyHtml: interpolateCertificateHtml(typeConfig.bodyHtml, {
      recipientName: buildRecipientName(registration),
      conferenceTitle: `${branding.heroTitle} ${branding.heroSubtitle}`.trim(),
      conferenceDates: dateLabels.receipt || dateLabels.long,
      conferenceLocation: dateLabels.location,
      certificateNumber: certNumber,
      issueDate,
      issueDateFormatted: formatIssueDate(issueDate),
      roleLine: typeConfig.roleLine || '',
      presentationTitle: acceptedAbstract?.title || '',
      presentationType: acceptedAbstract?.presentation_type || 'oral',
    }),
  };

  return base;
};

const passesAttendanceGate = (registration, certSettings, conferenceSettings) => {
  if (certSettings.require_check_in) {
    return Boolean(registration.checked_in);
  }
  if (certSettings.available_after_conference_end) {
    return isConferenceEnded(conferenceSettings?.conference_end);
  }
  return true;
};

const getAttendanceGateMessage = (certSettings) => {
  if (certSettings.require_check_in) {
    return 'Check in at the venue first to unlock your certificate.';
  }
  if (certSettings.available_after_conference_end) {
    return 'Certificates become available after the conference ends.';
  }
  return 'Certificate is not yet available.';
};

export const getCertificateEligibility = (
  registration,
  certificateType,
  conferenceSettings = {},
  abstracts = []
) => {
  const certSettings = mergeCertificateSettings(conferenceSettings.certificate_settings);
  const regType = getRegistrationType(registration);

  if (!certSettings.enabled_types?.[certificateType]) {
    return { eligible: false, reason: 'This certificate type is not available.' };
  }

  if (registration.payment_status !== 'completed') {
    return { eligible: false, reason: 'Payment must be completed before downloading a certificate.' };
  }

  if (certificateType === 'exhibitor') {
    if (regType !== 'exhibitor') {
      return { eligible: false, reason: 'Exhibitor certificates are only for exhibitor registrations.' };
    }
  } else if (certificateType === 'participant') {
    if (regType === 'exhibitor') {
      return { eligible: false, reason: 'Use the exhibitor certificate for this registration.' };
    }
  } else if (certificateType === 'presenter') {
    if (regType === 'exhibitor') {
      return { eligible: false, reason: 'Presenter certificates are not available for exhibitor registrations.' };
    }
    if (!getAcceptedAbstract(abstracts)) {
      return { eligible: false, reason: 'Presenter certificate requires an accepted abstract.' };
    }
  }

  if (!passesAttendanceGate(registration, certSettings, conferenceSettings)) {
    return { eligible: false, reason: getAttendanceGateMessage(certSettings) };
  }

  return { eligible: true, reason: '' };
};

export const getEligibleCertificateTypes = (
  registration,
  conferenceSettings = {},
  abstracts = []
) =>
  CERTIFICATE_TYPES.filter((type) => {
    const { eligible } = getCertificateEligibility(
      registration,
      type,
      conferenceSettings,
      abstracts
    );
    return eligible;
  });

export const getApplicableCertificateTypes = (registration) => {
  const regType = getRegistrationType(registration);
  if (regType === 'exhibitor') return ['exhibitor'];
  return ['participant', 'presenter'];
};

export const assertRegistrationOwnership = (registration, user) => {
  if (!registration) throw new Error('Registration not found.');
  if (!user) throw new Error('You must be logged in to download a certificate.');

  if (registration.uid && user.uid && registration.uid === user.uid) return;
  if (
    user.email &&
    registration.email &&
    registration.email.toLowerCase() === user.email.toLowerCase()
  ) {
    return;
  }
  if (registration.member_id && user.uid && registration.member_id === user.uid) return;

  throw new Error('You do not have access to this registration.');
};
